/** @jsxFrag React.Fragment */

/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useRef } from 'react';
import ReactMarkdown from 'react-markdown';

import classNames from 'classnames';

import { typography2 } from '@coursera/cds-core';
import type { Product } from '@coursera/event-pulse-types';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';
import type { EventData } from '@coursera/event-pulse/react';

import { CopySingleResponseLazy } from 'bundles/ai-coach-platform/components/building-blocks';
import {
  SearchTermHighlightRenderer,
  useSearchTermHighlights,
} from 'bundles/ai-coach-platform/components/patterns/chat/search/SearchTermHighlight';
import type { MessageTypes } from 'bundles/ai-coach-platform/components/patterns/chat/store/types';
import { isCoachMessage as isCoachMessageUtil } from 'bundles/ai-coach-platform/components/patterns/chat/utils/messageUtils';
import { CoachMode } from 'bundles/ai-coach-platform/constants';
import { useCoachTrackingContext } from 'bundles/ai-coach-platform/utils/tracking';

import _t from 'i18n!nls/ai-coach-platform';

const COACH_PRODUCT_REC_PARAM = 'coach-product-recs';
const COACH_SUPPORT_ARTICLE_PARAM = 'coach-support-article';
const COACH_SUPPORT_DIRECT_TO_AGENT_PARAM = 'coach-support-direct-to-agent';
const COACH_SUPPORT_UNROUTED_PARAM = 'coach-support-unrouted';
// temporary fix for support article links, see [COACH-1523]
// const ARTICLE_SLUG_REGEX = /https:\/\/learner\.coursera\.help\/hc\/articles\/([^?]+)/;
const ARTICLE_SLUG_REGEX = /https:\/\/www\.coursera\.support\/s\/article\/([^?]+)/;
const CODE_BLOCK_OR_INLINE_CODE_REGEX = /(```[\s\S]*?```|`[^`]*`)/g;
const ESCAPE_CHARS_REGEX = /[.*+?^${}()|[\]\\]/g;
const SEARCH_TERM_MARKER_REGEX = /^==(.+)==$/;

export type Props = {
  message?: MessageTypes;

  /**
   * Render additional custom content after the message content
   */
  children?: React.ReactNode;

  className?: string;

  isStreaming?: boolean;

  /**
   * Search term to highlight in the message
   */
  searchTerm?: string;
};

const styles = {
  root: css`
    padding: 0;
    max-width: 100%;

    .message-additional-content {
      width: 100%;
    }
  `,
  markdown: (isStreaming: boolean) => css`
    --code-bg-color: var(--cds-color-neutral-background-primary-weak);

    max-width: 100%;
    padding: 0;

    p,
    li,
    span {
      ${typography2.bodyPrimary}
      strong {
        font-size: 1em;
      }

      overflow-wrap: anywhere;
    }

    p {
      /* shows single line break as new line */
      white-space: pre-wrap;
      margin-top: 0;
    }

    pre {
      padding: var(--cds-spacing-100);
      margin: 0 0 var(--cds-spacing-100);
      font-size: 0.8rem;
      line-height: var(--cds-line-height-300);
      word-break: break-all;
      word-wrap: break-word;
      background-color: var(--code-bg-color);
      color: var(--cds-color-neutral-primary);
      border-radius: var(--cds-border-radius-100);
      overflow-x: auto;
    }

    code {
      padding: 0.2rem 0.4rem;
      font-size: 0.85rem;
      color: inherit;
      white-space: unset;
      background-color: var(--code-bg-color);
      border-radius: var(--cds-border-radius-100);
    }

    img {
      width: 100%;
      -o-object-fit: contain;
      object-fit: contain;
    }

    table {
      width: 100%;
      border-collapse: collapse;
      border: none;
      display: table; /* Reset to default table display */

      thead {
        tr {
          border-bottom: 1px solid var(--cds-color-neutral-disabled-strong);
        }

        th {
          font-weight: bold;
        }
      }

      tr:not(:last-child) {
        border-bottom: 1px solid var(--cds-color-neutral-disabled);
      }

      th,
      td {
        ${typography2.bodyPrimary}
        text-align: left;
        padding: var(--cds-spacing-100);
      }
    }

    .table-container {
      width: 100%;
      overflow-x: auto;
      margin: var(--cds-spacing-200) 0;
      -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
    }

    /* animate streamed content, temp skip code blocks due to re-rendering issues */
    p,
    li,
    tr,
    th {
      animation: ${isStreaming ? 'var(--labs-animation-fade-in)' : 'none'};
    }

    li p {
      /* prevent double animation for P inside LI */
      animation: none;
    }
  `,
  codeBlock: css`
    position: relative;
    width: 100%;

    .coach-copy-single-response {
      position: absolute;
      z-index: 1;
      top: 4px;
      right: 4px;
      width: 32px;
      height: 32px;

      button {
        width: 100%;
        height: 100%;
      }
    }

    /* visually hide copy button, but not from screen readers */
    &.hidden .coach-copy-single-response {
      clip: rect(0 0 0 0);
      clip-path: inset(50%);
      height: 1px;
      overflow: hidden;
      position: absolute;
      white-space: nowrap;
      width: 1px;
    }
  `,
};

const isContentRecLink = (linkHref: URL) => {
  return linkHref.searchParams.get('linkType') === COACH_PRODUCT_REC_PARAM;
};

const isSupportLink = (linkHref: URL) => {
  const validLinkTypes = [
    COACH_SUPPORT_ARTICLE_PARAM,
    COACH_SUPPORT_DIRECT_TO_AGENT_PARAM,
    COACH_SUPPORT_UNROUTED_PARAM,
  ];
  return validLinkTypes.includes(linkHref.searchParams.get('linkType') ?? '');
};

const getSupportLinkType = (linkHref: URL) => {
  switch (linkHref.searchParams.get('linkType')) {
    case COACH_SUPPORT_ARTICLE_PARAM:
      return 'article';
    case COACH_SUPPORT_DIRECT_TO_AGENT_PARAM:
      return 'direct_to_agent';
    case COACH_SUPPORT_UNROUTED_PARAM:
      return 'unrouted';
    default:
      return '';
  }
};

const getSupportArticleSlug = (linkHref: URL) => {
  const articleSlugMatch = linkHref.toString().match(ARTICLE_SLUG_REGEX);
  const articleSlug = articleSlugMatch ? articleSlugMatch[1] : 'INVALID_SLUG';
  return articleSlug;
};

const ParagraphRenderer = (props: React.HTMLAttributes<HTMLParagraphElement>) => <p {...props} />;

const ContentRecLinkRenderer = ({ href, children, ...rest }: React.LinkHTMLAttributes<HTMLAnchorElement>) => {
  const linkHref = new URL(href as string);

  const trackingContext = useCoachTrackingContext()?.data;
  const track = useTracker();
  const trackingData: EventData<'view_coach_product_recommendation'> = {
    recommendationType: 'chat_link',
    product: {
      id: linkHref.searchParams.get('productId') as Product['id'],
      type: linkHref.searchParams.get('productType') as Product['type'],
    },
    coach: {
      mode: trackingContext?.mode ?? CoachMode.Basic,
    },
  };

  const linkVisibilityRef: React.MutableRefObject<HTMLAnchorElement | null> = useVisibilityTracker(
    'view_coach_product_recommendation',
    { ...trackingData }
  );

  const contentRecLinkProps = {
    ref: linkVisibilityRef,
    onClick: () => {
      track('click_coach_product_recommendation', {
        ...trackingData,
      });
    },
  };

  // clean up custom query params before rendering, these are only meant for FE eventing
  linkHref.searchParams.delete('linkType');
  linkHref.searchParams.delete('productType');
  linkHref.searchParams.delete('productId');

  return (
    <a href={linkHref.href} {...contentRecLinkProps} {...rest} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
};

const SupportLinkRenderer = ({ href, children, ...rest }: React.LinkHTMLAttributes<HTMLAnchorElement>) => {
  const linkHref = new URL(href as string);
  const supportLinkType = getSupportLinkType(linkHref);

  const track = useTracker();
  // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  const trackingData: EventData<'view_coach_support_link'> = {
    supportLinkType,
    ...(supportLinkType === 'article' && {
      article: {
        articleSlug: getSupportArticleSlug(linkHref),
      },
    }),
    coach: {
      mode: 'HELP_AND_SUPPORT',
    },
  };

  const linkVisibilityRef: React.MutableRefObject<HTMLAnchorElement | null> = useVisibilityTracker(
    'view_coach_support_link',
    { ...trackingData }
  );

  const supportLinkProps = {
    ref: linkVisibilityRef,
    onClick: () => {
      track('click_coach_support_link', {
        ...trackingData,
      });
    },
  };

  // clean up custom query param before rendering, this is only meant for FE eventing
  linkHref.searchParams.delete('linkType');

  return (
    <a href={linkHref.href} {...supportLinkProps} {...rest} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
};

const GeneralLinkRenderer = ({ href, children, ...rest }: React.LinkHTMLAttributes<HTMLAnchorElement>) => {
  const linkHref = new URL(href as string);

  const trackingContext = useCoachTrackingContext()?.data;
  const track = useTracker();
  const trackingData: EventData<'view_coach_general_link'> = {
    linkUrl: linkHref.href,
    coach: {
      mode: trackingContext?.mode ?? CoachMode.Basic,
    },
  };

  const linkVisibilityRef: React.MutableRefObject<HTMLAnchorElement | null> = useVisibilityTracker(
    'view_coach_general_link',
    { ...trackingData }
  );

  const generalLinkProps = {
    ref: linkVisibilityRef,
    onClick: () => {
      track('click_coach_general_link', {
        ...trackingData,
      });
    },
  };

  return (
    <a href={linkHref.href} {...generalLinkProps} {...rest} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
};

const LinkRenderer = ({ href, children, ...rest }: React.LinkHTMLAttributes<HTMLAnchorElement>) => {
  let linkHref: URL;

  try {
    linkHref = new URL(href ?? '');
  } catch {
    // handle any invalid links as plain-text
    return <span>{children}</span>;
  }

  if (isContentRecLink(linkHref)) {
    return (
      <ContentRecLinkRenderer {...rest} href={href}>
        {children}
      </ContentRecLinkRenderer>
    );
  }

  if (isSupportLink(linkHref)) {
    return (
      <SupportLinkRenderer {...rest} href={href}>
        {children}
      </SupportLinkRenderer>
    );
  }

  return (
    <GeneralLinkRenderer {...rest} href={href}>
      {children}
    </GeneralLinkRenderer>
  );
};

/**
 * Handles reference links as plain-text. We do not support links by reference, which are
 * indicated through square brackets e.g. "reference [1]"
 */
const LinkReferenceRenderer = ({ children }: { children: React.ReactNode }) => {
  return <React.Fragment>[{children}]</React.Fragment>;
};

/**
 * Highlight the search term in the code block. This is separately needed for code blocks
 * which are not part of text blocks that are preprocessed in `preprocessMarkdown`.
 */
function highlightInCode(code: string, searchTerm: string) {
  if (!searchTerm || searchTerm.length < 2) {
    return code;
  }

  const escaped = searchTerm.replace(ESCAPE_CHARS_REGEX, '\\$&');
  const regex = new RegExp(`(${escaped})`, 'gi');

  const parts = code.split(regex);
  return parts.map((part, i) =>
    regex.test(part) ? (
      <SearchTermHighlightRenderer key={`${part}-${i + 1}`}>{part}</SearchTermHighlightRenderer>
    ) : (
      <React.Fragment key={part}>{part}</React.Fragment>
    )
  );
}

const CodeRenderer = ({
  value = '',
  language = 'Unset',
  searchTerm = '',
}: {
  value?: string;
  language?: string;
  searchTerm?: string;
}) => {
  const [showCopyButton, setShowCopyButton] = React.useState(false);

  const preprocessedContent = React.useMemo(() => {
    return searchTerm ? highlightInCode(value, searchTerm) : value;
  }, [value, searchTerm]);

  return (
    <div
      css={styles.codeBlock}
      className={classNames(`language-${language}`, { hidden: !showCopyButton })}
      aria-label={_t('Code block language: #{language}', { language })}
      onMouseEnter={() => setShowCopyButton(true)}
      onMouseLeave={() => setShowCopyButton(false)}
    >
      <pre>{preprocessedContent}</pre>

      <React.Suspense fallback={null}>
        <CopySingleResponseLazy message={value} tooltip="Copy code" skipMetadata aria-label={_t('Copy code')} />
      </React.Suspense>
    </div>
  );
};

/**
 * Handles inline code rendering, also search highlighting if a search term is available
 */
const InlineCodeRenderer = ({ value = '', searchTerm = '' }: { value?: string; searchTerm?: string }) => {
  const preprocessedContent = React.useMemo(() => {
    return searchTerm ? highlightInCode(value, searchTerm) : value;
  }, [value, searchTerm]);

  if (!value) {
    return null;
  }

  return <code>{preprocessedContent}</code>;
};

const TableRenderer = ({ children }: { children: React.ReactNode }) => (
  <div className="table-container">
    <table>{children}</table>
  </div>
);

/**
 * Preprocess the markdown text to highlight the search term when available.
 * Example:
 *   Input:  "Learn React basics and use react hooks"
 *   Search term: "React"
 *   Output: "Learn *==React==* basics and use *==react==* hooks"
 *
 * The *==term==* syntax is chosen because:
 *   - Uses existing emphasis (*) syntax that ReactMarkdown handles
 *   - Custom == markers distinguish search highlighting from regular emphasis
 *   - Works within code blocks and other markdown elements
 *
 * NOTE: upgrading react-markdown > 6 will allow us full access to text nodes
 * and better compatibility with plugins like `remark-gfmh`.
 *
 * @param markdownText - The markdown text to preprocess.
 * @param searchTerm - The search term to highlight.
 * @returns The preprocessed markdown text.
 */
function preprocessMarkdown(markdownText?: string, searchTerm?: string) {
  if (!markdownText || !searchTerm || searchTerm.length < 2) {
    return markdownText;
  }

  const codeRegex = CODE_BLOCK_OR_INLINE_CODE_REGEX;
  const parts = markdownText.split(codeRegex);

  const escaped = searchTerm.replace(ESCAPE_CHARS_REGEX, '\\$&');
  const highlightRegex = new RegExp(`(${escaped})`, 'gi');

  return parts
    .map((part) => {
      // if it's a code block or inline code, leave it unchanged
      if (/^`/.test(part)) {
        return part;
      }

      // otherwise, wrap search term in *==term==*
      // we're overriding the emphasis renderer (*content*) with custom == markers to help
      // differentiate between emphasis and search term highlighting.
      return part.replace(highlightRegex, '*==$1==*');
    })
    .join('');
}

/**

 * Custom ReactMarkdown renderer that intercepts emphasis (*text*) to detect search markers.
 * This is where the preprocessing markers get converted to actual highlight components.
 *
 * How it works:
 * 1. ReactMarkdown processes *==term==* as emphasis tag
 * 2. This renderer checks if the content matches the search marker pattern
 * 3. If it matches ==term==, renders <SearchTermHighlightRenderer/>
 * 4. If not, renders regular <em> tag
 *
 * Examples:
 *
 * Search highlight:
 * Markdown: "*==React==*" → SearchTermHighlightRenderer("React")
 *
 * Regular emphasis:
 * Markdown: "*important*" → <em>important</em>
 *
 * This dual behavior allows normal emphasis to work while intercepting search markers.
 */
const EmphasisRenderer = ({ children }: { children: string[] }) => {
  // Join children into a string
  const text = children.map((child) => (typeof child === 'string' ? child : '')).join('');

  // only match on search term markers wrapper content
  const match = text.match(SEARCH_TERM_MARKER_REGEX);

  if (match) {
    // render as search highlighted term
    return <SearchTermHighlightRenderer>{match[1]}</SearchTermHighlightRenderer>;
  }

  // regular emphasis tag
  return <em>{children}</em>;
};

/**
 * Renders the body of the chat message, such as the rendered Markdown for the content, and any other
 * additional content following the message, such as recommended actions or navigation elements.
 */
const ChatMessageBody = ({ message, children, className, isStreaming = false, searchTerm = '' }: Props) => {
  const isCoachMessage = message ? isCoachMessageUtil(message?.sender) : false;
  const messageBodyRef = useRef<HTMLDivElement>(null);

  // Use the custom hook for search term highlighting
  useSearchTermHighlights(messageBodyRef, searchTerm);

  const preprocessedMarkdownText = React.useMemo(() => {
    return searchTerm ? preprocessMarkdown(message?.text, searchTerm) : message?.text;
  }, [searchTerm, message?.text]);

  // Memoize the renderers to prevent recreating them on every render
  const renderers = React.useMemo(() => {
    return {
      link: LinkRenderer,
      linkReference: LinkReferenceRenderer,
      // prevent heading formatting, although we may not need this after Gemini 1.5 [COACH-587]
      heading: ParagraphRenderer,
      paragraph: ParagraphRenderer,
      table: TableRenderer,
      emphasis: EmphasisRenderer,
      code: (props: React.HTMLAttributes<HTMLElement>) => <CodeRenderer {...props} searchTerm={searchTerm} />,
      inlineCode: (props: React.HTMLAttributes<HTMLElement>) => (
        <InlineCodeRenderer {...props} searchTerm={searchTerm} />
      ),
    };
  }, [searchTerm]);

  return (
    <div
      ref={messageBodyRef}
      css={styles.root}
      data-testid="coach-message-markdown"
      className={classNames(className, { 'coach-response': isCoachMessage })}
    >
      {message ? (
        <ReactMarkdown
          css={styles.markdown(isStreaming)}
          renderers={renderers}
          source={preprocessedMarkdownText}
          linkTarget="_blank"
        />
      ) : null}
      {children ? <div className="message-additional-content">{children}</div> : null}
    </div>
  );
};

export default ChatMessageBody;
